import React, { useState, useEffect } from 'react';
import { Download, Loader, Save, Calendar, Clock, MapPin, User, X, Check, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const AsignaturasPruebaAdmision = () => {

  const PALETA_COLORES = [
    '#3B82F6', // Azul
    '#10B981', // Verde
    '#F59E0B', // Naranja
    '#8B5CF6', // Morado
    '#EC4899', // Rosado
    '#EF4444', // Rojo
    '#06B6D4', // Cian
    '#14B8A6', // Teal
    '#6366F1', // Indigo
  ];

  const obtenerColorPorCodigo = (codigo) => {
    if (!codigo) return PALETA_COLORES[0];
    let hash = 0;
    for (let i = 0; i < codigo.length; i++) {
      hash = codigo.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % PALETA_COLORES.length;
    return PALETA_COLORES[index];
  };

  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gestion, setGestion] = useState(new Date().getFullYear());

  const [asignaturas, setAsignaturas] = useState([]);
  const [selecciones, setSelecciones] = useState({}); // Mapa: codigo_asignatura -> id_horario (asignacion)
  const [yaGuardo, setYaGuardo] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [seleccionHabilitada, setSeleccionHabilitada] = useState(true);
  const [periodoInfo, setPeriodoInfo] = useState(null);

  // Modal para selección de paralelo
  const [modalAsignatura, setModalAsignatura] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Días y Horas
  const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
  const generarHoras = () => {
    const horas = [];
    for (let h = 6; h <= 21; h++) {
      horas.push(`${String(h).padStart(2, '0')}:00`);
      horas.push(`${String(h).padStart(2, '0')}:30`);
    }
    return horas;
  };
  const horas = generarHoras();

  // Función para obtener el rango del slot (ej: "07:00 - 07:30")
  const getSlotRango = (horaInicio) => {
    const [h, m] = horaInicio.split(':').map(Number);
    let finH = h;
    let finM = m + 30;
    if (finM >= 60) {
      finM = 0;
      finH += 1;
    }
    const horaFin = `${finH.toString().padStart(2, '0')}:${finM.toString().padStart(2, '0')}`;
    return `${horaInicio} - ${horaFin}`;
  };

  useEffect(() => {
    verificarPeriodoSeleccion();
    loadData(true);

    // Auto-refresh en background cada 20 segundos para reflejar actualizaciones de Jefatura (cupos, horarios, etc.)
    const interval = setInterval(() => {
      loadData(false);
    }, 20000);

    return () => clearInterval(interval);
  }, [gestion]);

  const verificarPeriodoSeleccion = async () => {
    // Restricción: Solo validar fechas para el año actual. Años futuros/pasados bloqueados por defecto.
    const currentYear = new Date().getFullYear();
    if (gestion !== currentYear) {
      setSeleccionHabilitada(false);
      setPeriodoInfo(null);
      // Opcional: setMensaje("Inscripción solo habilitada para la gestión en curso.");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      // const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Inscripción a prueba de admisión académica')}`, { headers });
      const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Prueba de admisión académica y admisión especial')}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSeleccionHabilitada(data.habilitado);
        setPeriodoInfo(data);
      } else {
        // Fallback si falla endpoint
        setSeleccionHabilitada(false);
      }
    } catch (error) {
      console.error('Error verificando periodo:', error);
      setSeleccionHabilitada(false);
    }
  };

  const loadData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // 1. Obtener Horarios Disponibles (Flat List)
      const resHorarios = await fetch(`${API_URL}/estudiante/horarios/disponibles/${gestion}?tipo=paa`, { headers });
      let asignaturasGrouped = [];

      if (resHorarios.ok) {
        const data = await resHorarios.json();
        const rawHorarios = data.horarios || [];

        // Agrupar por Asignatura (Código) y luego por Paralelo
        const groups = {};
        rawHorarios.forEach(h => {
          const codigo = h.asignatura || h.codigo_asignatura;
          if (!groups[codigo]) {
            groups[codigo] = {
              codigo: codigo,
              nombre: h.nombre_asignatura || h.asignatura,
              paralelosMap: {} // Map by paralelo name
            };
          }

          const paraleloKey = h.id.toString(); // Agrupar por ID real para no mezclar horarios diferentes creados con el mismo nombre

          // if (!groups[codigo].paralelosMap[paraleloKey]) {
          //   groups[codigo].paralelosMap[paraleloKey] = {
          //     id_asignacion: h.id, // ID exacto del horario
          //     paralelo: h.paralelo || 'A',
          //     docente: h.docente || h.docente_nombre || 'Por asignar',
          //     bloques: [], // Múltiples bloques si usa horarios_detalle
          //     sala: h.sala || h.aula || '-',
          //     color: h.color || '#3B82F6',
          //     capacidad: parseInt(h.capacidad) || 30,
          //     inscritos: parseInt(h.inscritos) || 0
          //   };
          // }
          if (!groups[codigo].paralelosMap[paraleloKey]) {
            groups[codigo].paralelosMap[paraleloKey] = {
              id_asignacion: h.id,
              paralelo: h.paralelo || 'A',
              docente: h.docente || h.docente_nombre || 'Por asignar',
              bloques: [],
              sala: h.sala || h.aula || '-',
              // MODIFICACIÓN CRÍTICA AQUÍ: Forzamos el uso de nuestra paleta basada en el código de la materia
              color: obtenerColorPorCodigo(codigo),
              capacidad: parseInt(h.capacidad) || 30,
              inscritos: parseInt(h.inscritos) || 0
            };
          }

          // Analizar si hay horarios_detalle (ej: lunes 10-12, martes 8-10)
          if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
            h.horarios_detalle.forEach(det => {
              groups[codigo].paralelosMap[paraleloKey].bloques.push({
                dia: det.dia,
                hora_inicio: det.hora_inicio || h.hora_inicio,
                hora_fin: det.hora_fin || h.hora_fin
              });
            });
          } else {
            // Lógica antigua: días separados por coma con la misma hora
            const diasArray = h.dia_semana ? h.dia_semana.split(',').map(d => d.trim()) : [];
            diasArray.forEach(dia => {
              groups[codigo].paralelosMap[paraleloKey].bloques.push({
                dia: dia,
                hora_inicio: h.hora_inicio,
                hora_fin: h.hora_fin
              });
            });
          }
        });

        // Convert to array structure
        asignaturasGrouped = Object.values(groups).map(g => ({
          codigo: g.codigo,
          nombre: g.nombre,
          paralelos: Object.values(g.paralelosMap)
            .map(p => ({
              ...p,
              // Collect unique days from all blocks
              dias: [...new Set(p.bloques.map(b => b.dia))],
              // Use first block times as display (or min/max)
              hora_inicio: p.bloques.length > 0 ? p.bloques.reduce((min, b) => b.hora_inicio < min ? b.hora_inicio : min, p.bloques[0].hora_inicio) : '',
              hora_fin: p.bloques.length > 0 ? p.bloques.reduce((max, b) => b.hora_fin > max ? b.hora_fin : max, p.bloques[0].hora_fin) : '',
            }))
        })).filter(asig => asig.paralelos.length > 0); // Ocultar asignatura si no tiene paralelos disponibles
      }
      setAsignaturas(asignaturasGrouped);

      // 2. Verificar Selecciones Guardadas
      const resMiHorario = await fetch(`${API_URL}/estudiante/horarios/${gestion}`, { headers });
      if (resMiHorario.ok) {
        const data = await resMiHorario.json();
        if (data.horario && data.horario.horario_seleccionado) {
          setYaGuardo(true);
          const prevSelecciones = {};
          // data.horario.horario_seleccionado es array de objetos completos
          data.horario.horario_seleccionado.forEach(sel => {
            const codigo = sel.asignatura || sel.codigo; // El código guardado
            prevSelecciones[codigo] = sel.id;
          });
          setSelecciones(prevSelecciones);
        }
      }

    } catch (error) {
      console.error('Error cargando datos:', error);
      if (showLoader) setMensaje('Error cargando la información.');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Manejo de Selección
  const handleSeleccionParalelo = (codigoAsignatura, idHorario) => {
    if (yaGuardo) return;

    // Obtener información del paralelo para validar cupos antes de seleccionar
    const asigTarget = asignaturas.find(a => a.codigo === codigoAsignatura);
    const isCurrentlySelected = selecciones[codigoAsignatura] === idHorario;

    if (asigTarget) {
      const paraleloTarget = asigTarget.paralelos.find(p => p.id_asignacion === idHorario);
      if (!isCurrentlySelected && paraleloTarget && paraleloTarget.inscritos >= paraleloTarget.capacidad) {
        setMensaje(`El paralelo ${paraleloTarget.paralelo} de ${asigTarget.nombre} ya no tiene cupos disponibles.`);
        return;
      }
    }

    setSelecciones(prev => ({
      ...prev,
      [codigoAsignatura]: isCurrentlySelected ? null : idHorario
    }));
    setModalAsignatura(null);
  };

  const getParaleloSeleccionado = (asig) => {
    const idHorario = selecciones[asig.codigo];
    if (!idHorario) return null;
    return asig.paralelos.find(p => p.id_asignacion === idHorario);
  };

  // Helpers para Grid
  const normalizeDia = (d) => {
    if (!d) return '';
    return d.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  }

  const getHorariosEnCelda = (dia, hora) => {
    const resultado = [];
    const diaNorm = normalizeDia(dia);
    asignaturas.forEach(asig => {
      const idSeleccionado = selecciones[asig.codigo];
      if (!idSeleccionado) return;

      const paralelo = asig.paralelos.find(p => p.id_asignacion === idSeleccionado);
      if (paralelo && paralelo.bloques) {
        paralelo.bloques.forEach(bloque => {
          if (normalizeDia(bloque.dia) === diaNorm) {
            const horaInicio = bloque.hora_inicio?.substring(0, 5).trim();
            const horaFin = bloque.hora_fin?.substring(0, 5).trim();
            if (hora >= horaInicio && hora < horaFin) {
              resultado.push({ ...paralelo, codigo: asig.codigo, nombre: asig.nombre });
            }
          }
        });
      }
    });
    return resultado;
  };

  const guardarHorario = async () => {
    if (!seleccionHabilitada) {
      setMensaje(`El periodo de selección ha finalizado.`);
      return;
    }
    const horariosIds = Object.values(selecciones).filter(Boolean);
    if (horariosIds.length === 0) {
      setMensaje('Seleccione al menos una materia.');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      // Construir array de objetos selecionados basándonos en los IDs
      const objetosSeleccionados = [];
      asignaturas.forEach(asig => {
        const p = getParaleloSeleccionado(asig);
        if (p) {
          objetosSeleccionados.push({
            id: p.id_asignacion,
            asignatura: asig.codigo,
            nombre_asignatura: asig.nombre,
            paralelo: p.paralelo,
            docente: p.docente,
            dia_semana: p.dias.join(','),
            hora_inicio: p.hora_inicio,
            hora_fin: p.hora_fin,
            sala: p.sala,
            color: p.color,
            horarios_detalle: p.bloques
          });
        }
      });

      const response = await fetch(`${API_URL}/estudiante/horarios`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          gestion,
          horario_seleccionado: objetosSeleccionados,
          tipo: 'preadmision'
        })
      });

      if (response.ok) {
        setYaGuardo(true);
        setMensaje('¡Horario guardado correctamente!');
        loadData(false); // Recargar sin loader para suavidad
      } else {
        const err = await response.json();
        setMensaje(err.message || 'Error al guardar.');
      }
    } catch (error) {
      setMensaje('Error de conexión al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const exportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // 1. Preparar la Grilla Semanal (Horas vs Días)
      const gridData = [];

      // Encabezados de Días directamente en la primera fila
      gridData.push(['Hora', ...dias]);

      // Llenar filas por cada slot de tiempo
      horas.forEach(hora => {
        const row = [getSlotRango(hora)];
        dias.forEach(dia => {
          const celdas = getHorariosEnCelda(dia, hora);
          if (celdas.length > 0) {
            // Formateamos el contenido de la celda: CODIGO - NOMBRE (PARALELO)
            row.push(celdas.map(h => `${h.codigo}: ${h.nombre} (P:${h.paralelo})`).join(' / '));
          } else {
            row.push('');
          }
        });
        gridData.push(row);
      });

      const wsGrid = XLSX.utils.aoa_to_sheet(gridData);
      XLSX.utils.book_append_sheet(wb, wsGrid, "Horario");

      // 2. Hoja de Detalle de Materias (Lista simple)
      const listData = [];
      asignaturas.forEach(asig => {
        const paralelo = getParaleloSeleccionado(asig);
        if (paralelo) {
          listData.push({
            'Codigo': asig.codigo || '-',
            'Asignatura': asig.nombre || '-',
            'Paralelo': paralelo.paralelo || '-',
            'Docente': paralelo.docente || '-',
            'Info': paralelo.bloques && Array.isArray(paralelo.bloques)
              ? paralelo.bloques.map(d => `${d.dia} ${d.hora_inicio?.substring(0, 5)}-${d.hora_fin?.substring(0, 5)}`).join(', ')
              : `${paralelo.dias?.join(', ') || '-'} ${paralelo.hora_inicio?.substring(0, 5)}-${paralelo.hora_fin?.substring(0, 5)}`
          });
        }
      });

      if (listData.length > 0) {
        const wsList = XLSX.utils.json_to_sheet(listData);
        XLSX.utils.book_append_sheet(wb, wsList, "Detalle");
      }

      // MÉTODO DE ESCRITURA ESTÁNDAR (El más compatible en Windows)
      XLSX.writeFile(wb, "Horario_PruebaAdmision.xlsx");
      setShowDownloadModal(false);
    } catch (error) {
      console.error("Error al exportar:", error);
      setMensaje("Error al generar el Excel: " + error.message);
      setShowDownloadModal(false);
    }
  };

  const cantidadSeleccionadas = Object.values(selecciones).filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="animate-spin mr-2" /> Cargando oferta académica...
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Inscripción Asignaturas </h1>
        <div className="flex gap-2">
          <select
            value={gestion}
            onChange={(e) => setGestion(parseInt(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
          >
            {[new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {yaGuardo && (
            <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download size={16} />
            </button>
          )}
        </div>
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded-lg ${mensaje.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
          {mensaje}
        </div>
      )}

      {!seleccionHabilitada && !yaGuardo && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg">
          El periodo de selección de asignaturas ha finalizado o no está activo.
        </div>
      )}

      {/* Grid Layout Principal - Asignaturas más chicas (1fr), Vista Previa más grande (2fr) */}
      <div className={`grid gap-6 ${yaGuardo ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-[1fr_2fr]'}`}>

        {/* Columna Izquierda: Lista de Materias (Solo si no ha guardado) */}
        {!yaGuardo && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar size={20} /> Asignaturas Disponibles
            </h2>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {asignaturas.length === 0 ? (
                <p className="text-gray-500 py-8 text-center">No hay asignaturas disponibles.</p>
              ) : (
                asignaturas.map(asig => {
                  const pSeleccionado = getParaleloSeleccionado(asig);
                  return (
                    <div
                      key={asig.codigo}
                      onClick={() => !yaGuardo && seleccionHabilitada && setModalAsignatura(asig)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${pSeleccionado ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-blue-400 bg-white'
                        } ${(!seleccionHabilitada || yaGuardo) ? 'opacity-75 cursor-default' : ''}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-mono text-xs text-gray-500">{asig.codigo}</p>
                          <p className="font-semibold text-gray-800 text-sm">{asig.nombre}</p>

                          {pSeleccionado ? (
                            <div className="mt-1 text-xs bg-white p-1.5 rounded border">
                              <div className="flex justify-between items-center mb-1">
                                <p className="font-bold text-green-700">Paralelo {pSeleccionado.paralelo}</p>
                                <span className="text-[10px] text-gray-500 font-medium">Cupos: {pSeleccionado.inscritos}/{pSeleccionado.capacidad}</span>
                              </div>
                              <p className="text-gray-600">{pSeleccionado.docente}</p>
                              {pSeleccionado.bloques && pSeleccionado.bloques.length > 0 ? (
                                pSeleccionado.bloques.map((b, i) => (
                                  <p key={i} className="text-gray-500 text-xs">
                                    {b.dia} {b.hora_inicio?.substring(0, 5)}-{b.hora_fin?.substring(0, 5)}
                                  </p>
                                ))
                              ) : (
                                <p className="text-gray-500 text-xs">{pSeleccionado.dias.join(', ')} {pSeleccionado.hora_inicio?.substring(0, 5)}-{pSeleccionado.hora_fin?.substring(0, 5)}</p>
                              )}
                            </div>
                          ) : (
                            <p className="mt-1 text-xs text-orange-600">
                              {asig.paralelos.length} Paralelo(s) disponibles - Click para seleccionar
                            </p>
                          )}
                        </div>
                        {pSeleccionado && (
                          <div className="flex flex-col items-end justify-between ml-2 h-full gap-3">
                            <Check className="text-green-600 mt-1" size={20} />
                            {!yaGuardo && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const nuevasSel = { ...selecciones };
                                  delete nuevasSel[asig.codigo];
                                  setSelecciones(nuevasSel);
                                }}
                                className="text-[10px] text-red-500 hover:text-red-700 font-semibold px-2 py-1 bg-red-50 rounded border border-red-100 hover:bg-red-100 transition-colors"
                              >
                                Quitar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={guardarHorario}
                disabled={!seleccionHabilitada || saving || cantidadSeleccionadas === 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:bg-gray-300 font-medium"
              >
                {saving ? <Loader className="animate-spin" /> : <Save size={18} />}
                Guardar Horario ({cantidadSeleccionadas}/{asignaturas.length})
              </button>
            </div>
          </div>
        )}

        {/* Columna Derecha: Vista Previa Horario (Más grande) */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {yaGuardo ? 'Mi Horario de Clases' : 'Vista Previa del Horario'}
          </h2>

          {cantidadSeleccionadas === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/30">
              <div className="text-4xl mb-4">🗓️</div>
              <p className="text-gray-400 text-sm font-medium">El horario se pintará aquí una vez que</p>
              <p className="text-gray-500 font-bold">selecciones tus materias</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 w-16">Hora</th>
                    {dias.map(d => <th key={d} className="border p-2 min-w-[100px] text-center">{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {horas.map((hora, hIdx) => (
                    <tr key={hora}>
                      <td className="border border-gray-200 p-2 text-center text-gray-500 font-mono bg-gray-50 whitespace-nowrap text-[11px]">
                        {getSlotRango(hora)}
                      </td>
                      {dias.map(dia => {
                        const enCelda = getHorariosEnCelda(dia, hora);
                        // Lógica de Continuación (Seamless) para la celda entera
                        let esContinuacion = false;
                        if (hIdx > 0 && enCelda.length === 1) {
                          const antes = getHorariosEnCelda(dia, horas[hIdx - 1]);
                          if (antes.length === 1 && antes[0].id_asignacion === enCelda[0].id_asignacion) {
                            esContinuacion = true;
                          }
                        }

                        return (
                          <td
                            key={`${dia}-${hora}`}
                            className={`p-0 border-x border-gray-200 h-14 align-top min-w-[140px] ${esContinuacion ? 'border-t-0' : 'border-t border-gray-100'} ${hIdx === horas.length - 1 ? 'border-b border-gray-200' : ''}`}
                          >
                            <div className={`h-full w-full flex flex-col ${esContinuacion ? '-mt-[1px]' : ''}`}>
                              {enCelda.map((h, i) => {
                                // Verificación individual en caso de haber múltiples asignaturas en la misma celda
                                const antes = hIdx > 0 ? getHorariosEnCelda(dia, horas[hIdx - 1]) : [];
                                const esContIndiv = antes.some(a => a.id_asignacion === h.id_asignacion);

                                return (
                                  <div
                                    key={i}
                                    className={`flex-1 text-[10px] p-2 leading-tight shadow-inner flex flex-col justify-center transition-all ${esContIndiv ? 'pt-0' : ''}`}
                                    style={{
                                      // SI YA GUARDÓ: Muestra el color sólido asignado.
                                      // SI NO HA GUARDADO (VISTA PREVIA): Muestra la versión translúcida de SU PROPIO color.
                                      backgroundColor: yaGuardo ? h.color : hexToRgba(h.color, 0.2),
                                      borderTop: esContIndiv ? 'none' : 'rgba(255,255,255,0.3) 1px solid',
                                      // Agrega una barra lateral izquierda con el color sólido en la vista previa para diferenciarlo mejor
                                      borderLeft: yaGuardo ? 'none' : `4px solid ${h.color}`,
                                      color: yaGuardo ? 'white' : '#1e293b'
                                    }}
                                    title={`${h.nombre} - ${h.docente}\nSala: ${h.sala}`}
                                  >
                                    {yaGuardo ? (
                                      !esContIndiv && (
                                        <>
                                          <div className="truncate uppercase text-[10px] font-bold">{h.codigo}</div>
                                          <div className="truncate max-w-full text-[9px] uppercase tracking-tighter opacity-90">{h.nombre}</div>
                                          <div className="text-[8px] mt-0.5 opacity-80">P:{h.paralelo} | S:{h.sala || '-'}</div>
                                        </>
                                      )
                                    ) : (
                                      !esContIndiv && (
                                        <div className="h-full flex flex-col items-center justify-center font-bold opacity-75 text-[9px] uppercase tracking-wider text-center">
                                          <span style={{ color: h.color }} className="font-mono">{h.codigo}</span>
                                          <span className="text-gray-400 font-normal text-[8px]">Reservado</span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                                // return (
                                //   <div
                                //     key={i}
                                //     className={`flex-1 text-[10px] p-2 leading-tight shadow-inner flex flex-col justify-center transition-all ${esContIndiv ? 'pt-0' : ''}`}
                                //     style={{
                                //       backgroundColor: yaGuardo ? (h.color || '#3B82F6') : 'rgba(59, 130, 246, 0.25)',
                                //       borderTop: esContIndiv ? 'none' : 'rgba(255,255,255,0.1) 1px solid',
                                //       color: yaGuardo ? 'white' : '#1e40af'
                                //     }}
                                //     title={`${h.nombre} - ${h.docente}\nSala: ${h.sala}`}
                                //   >
                                //     {yaGuardo ? (
                                //       !esContIndiv && (
                                //         <>
                                //           <div className="truncate uppercase text-[10px] font-bold">{h.codigo}</div>
                                //           <div className="truncate max-w-full text-[9px] uppercase tracking-tighter opacity-90">{h.nombre}</div>
                                //           <div className="text-[8px] mt-0.5 opacity-80">P:{h.paralelo} | S:{h.sala || '-'}</div>
                                //         </>
                                //       )
                                //     ) : (
                                //       !esContIndiv && (
                                //         <div className="h-full flex items-center justify-center italic font-bold opacity-40 text-[9px] uppercase tracking-widest text-center">
                                //           Reservado
                                //         </div>
                                //       )
                                //     )}
                                //   </div>
                                // );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Selección de Paralelo */}
      {modalAsignatura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold text-blue-900">{modalAsignatura.nombre}</h2>
              <button onClick={() => setModalAsignatura(null)}><X size={20} /></button>
            </div>
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {modalAsignatura.paralelos.map(p => {
                const isSelected = selecciones[modalAsignatura.codigo] === p.id_asignacion;
                return (
                  <div
                    key={p.id_asignacion}
                    onClick={() => handleSeleccionParalelo(modalAsignatura.codigo, p.id_asignacion)}
                    className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-blue-400'} ${!isSelected && p.inscritos >= p.capacidad ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-bold text-blue-700 text-lg flex items-center gap-2">
                            Paralelo {p.paralelo}
                            {!isSelected && p.inscritos >= p.capacidad && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold border border-red-200">
                                Cupos Completos
                              </span>
                            )}
                          </p>
                          <span className={`text-sm font-semibold px-2 py-1 rounded-full border ${p.inscritos >= p.capacidad ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                            Cupos: {p.inscritos} / {p.capacidad}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-800 flex items-center gap-1.5">
                            <User size={14} className="text-gray-500" />
                            <span className="font-medium">{p.docente}</span>
                          </p>
                          <p className="text-sm text-gray-700 flex items-start gap-1.5">
                            <Clock size={14} className="text-gray-500 mt-0.5" />
                            <div className="space-y-0.5">
                              {p.bloques && p.bloques.length > 0 ? (
                                p.bloques.map((b, idx) => (
                                  <span key={idx} className="block">
                                    {b.dia}: {b.hora_inicio?.substring(0, 5)} - {b.hora_fin?.substring(0, 5)}
                                  </span>
                                ))
                              ) : (
                                <span>{p.dias.join(', ')} {p.hora_inicio?.substring(0, 5)} - {p.hora_fin?.substring(0, 5)}</span>
                              )}
                            </div>
                          </p>
                          <p className="text-sm text-gray-700 flex items-center gap-1.5">
                            <MapPin size={14} className="text-gray-500" />
                            <span>Sala: {p.sala}</span>
                          </p>
                        </div>
                      </div>
                      {isSelected && <Check size={24} className="text-green-600 flex-shrink-0 ml-2" />}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setModalAsignatura(null)} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmación Descarga */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
            <AlertTriangle className="mx-auto mb-4 text-yellow-500" size={48} />
            <h3 className="text-lg font-bold mb-2">Aviso Importante</h3>
            <p className="text-sm text-gray-600 mb-6">
              Nota: El siguiente horario se presenta únicamente con fines informativos y no tiene validez curricular.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowDownloadModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">Cancelar</button>
              <button onClick={exportToExcel} className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800">Aceptar y Descargar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsignaturasPruebaAdmision;
